import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { Tag, TagDraft, useCreateTag, useDeleteTag, useTags, useUpdateTag } from '../../queries/tags';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

interface ManageTagsDialogProps {
  onClose: () => void;
}

const EMPTY_DRAFT: TagDraft = { name: '', category: 'geographic', description: '' };
const CATEGORIES = ['geographic', 'classification', 'package', 'schedule'];

export function ManageTagsDialog({ onClose }: ManageTagsDialogProps) {
  const { data: tags = [], isLoading } = useTags();
  const create = useCreateTag();
  const update = useUpdateTag();
  const remove = useDeleteTag();

  const [editing, setEditing] = useState<(TagDraft & { id?: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Tag | null>(null);
  const { success, error: toastError } = useToast();

  const handleSave = () => {
    if (!editing) return;
    setError(null);
    const onSettled = {
      onSuccess: () => setEditing(null),
      onError: (err: unknown) => setError(extractErrorMessage(err, 'Save failed.')),
    };
    if (editing.id) {
      update.mutate({ ...editing, id: editing.id }, onSettled);
    } else {
      create.mutate(editing, onSettled);
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted tag "${deleting.name}".`);
        setDeleting(null);
      },
      onError: (err) => {
        toastError(extractErrorMessage(err, 'Delete failed. It may still be in use.'));
        setDeleting(null);
      },
    });
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Manage tags</Dialog.Title>

          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
              {tags.map((tag) =>
                editing?.id === tag.id ? (
                  <div key={tag.id} className="flex items-center gap-2 rounded-md border border-border p-2">
                    <input
                      className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm"
                      value={editing.name ?? ''}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    />
                    <select
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                      value={editing.category ?? 'geographic'}
                      onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <button onClick={handleSave} className="text-xs font-medium text-primary hover:underline">
                      Save
                    </button>
                    <button onClick={() => setEditing(null)} className="text-xs text-muted-foreground hover:text-foreground">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div key={tag.id} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-accent">
                    <button onClick={() => setEditing({ id: tag.id, name: tag.name, category: tag.category, description: tag.description })} className="text-left text-sm">
                      <span className="font-medium">{tag.name}</span>{' '}
                      <span className="text-xs text-muted-foreground">({tag.category})</span>
                    </button>
                    <button onClick={() => setDeleting(tag)} className="text-muted-foreground hover:text-destructive" title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ),
              )}
              {tags.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No tags yet.</p>}
            </div>
          )}

          {editing && !editing.id && (
            <div className="mt-3 flex items-center gap-2 rounded-md border border-border p-2">
              <input
                className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm"
                placeholder="Tag name"
                value={editing.name ?? ''}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <select
                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                value={editing.category ?? 'geographic'}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button onClick={handleSave} disabled={create.isPending} className="text-xs font-medium text-primary hover:underline">
                Add
              </button>
              <button onClick={() => setEditing(null)} className="text-xs text-muted-foreground hover:text-foreground">
                Cancel
              </button>
            </div>
          )}

          <div className="mt-4 flex justify-between">
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing({ ...EMPTY_DRAFT })}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                <Plus className="h-3.5 w-3.5" />
                Add tag
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="ml-auto rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Close
            </button>
          </div>
          <ConfirmDialog
            open={!!deleting}
            onOpenChange={(open) => !open && setDeleting(null)}
            title="Delete tag?"
            description={`"${deleting?.name}" will be removed. If it's assigned to any track or package it may be blocked. This cannot be undone.`}
            confirmLabel="Delete"
            destructive
            busy={remove.isPending}
            onConfirm={confirmDelete}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
